import { useBlocker, useRouter } from "@tanstack/solid-router";
import { onCleanup } from "solid-js";

/**
 * Back-button-cierra-overlay (patrón premium PWA/móvil).
 *
 * Cuando un overlay (dialog/sheet/command-palette/drawer no-ruteado) está abierto
 * y el usuario pulsa "atrás" del navegador, queremos CERRAR el overlay en vez de
 * navegar en el historial.
 *
 * IMPORTANTE — por qué NO manipulamos el historial a mano: TanStack Router parchea
 * la API History (en la instancia `window.history`) y, además de mantener un
 * índice/clave por entrada, dispara `router.load()` en cada entrada nueva. Empujar
 * una entrada manual:
 *   - si duplica el índice del router → `delta = 0` en el popstate → bucle → CUELGUE;
 *   - si lo incrementa → el router lo trata como navegación real, recarga la ruta y
 *     REMONTA el componente, reseteando la señal local del overlay → el overlay NO ABRE.
 * Por eso usamos el mecanismo propio del router: `useBlocker`. No tocamos el
 * historial al abrir (el overlay abre normal); solo interceptamos la acción "BACK":
 * cerramos el overlay del tope y cancelamos la navegación (el router hace `go(1)`).
 *
 * LIFO: varios overlays anidados registran su entrada; "atrás" cierra el más reciente
 * que siga abierto. No interfiere con navegación real (PUSH/REPLACE/FORWARD pasan).
 */

interface OverlayEntry {
  isOpen: () => boolean;
  close: () => void;
}

const stack: OverlayEntry[] = [];

/**
 * Lógica pura y testeable: ante una acción de navegación, decide si debe CERRAR el
 * overlay (y devolver `true` para BLOQUEAR la navegación) en vez de navegar.
 * Solo intercepta "BACK", y solo si `entry` es el overlay abierto más reciente.
 */
export function resolveBackDismiss(
  action: string,
  entry: OverlayEntry,
  overlays: readonly OverlayEntry[] = stack,
): boolean {
  if (action !== "BACK") {
    return false;
  }
  const topmostOpen = overlays.findLast((candidate) => candidate.isOpen());
  if (topmostOpen === entry) {
    entry.close();
    return true;
  }
  return false;
}

/**
 * @param isOpen  señal reactiva: `true` mientras el overlay está abierto.
 * @param close   cierra el overlay (p.ej. `() => onOpenChange(false)`).
 */
export function useBackDismiss(isOpen: () => boolean, close: () => void): void {
  // Sin RouterProvider (tests de componentes aislados / SSR) el blocker no aplica;
  // el back-dismiss es una mejora progresiva, así que hacemos no-op. En producción
  // el overlay siempre se monta dentro del router.
  if (!useRouter({ warn: false })) {
    return;
  }

  // CC-19: el cierre se DIFIERE a una macrotarea para que corra DESPUÉS del
  // `history.go(1)` que la librería ejecuta al bloquear el BACK. Así un `close()`
  // que navega (overlays direccionables por URL que togglean un search-param con
  // `replace:true`) no compite con esa restauración y no deja URL/estado
  // desincronizados (overlay que rebota abierto al recargar/re-navegar). Para
  // overlays de señal local el diferido es imperceptible.
  const deferredClose = () => {
    setTimeout(close, 0);
  };
  const entry: OverlayEntry = { isOpen, close: deferredClose };
  stack.push(entry);
  onCleanup(() => {
    const idx = stack.indexOf(entry);
    if (idx !== -1) {
      stack.splice(idx, 1);
    }
  });

  // `enableBeforeUnload: false` → no queremos el prompt nativo "¿salir del sitio?".
  useBlocker({
    enableBeforeUnload: false,
    shouldBlockFn: ({ action }) => resolveBackDismiss(action, entry),
  });
}
